/**
 * Cart Recovery Analytics Tab - Refactored
 * Shows abandonment metrics, recovery rates, and settings
 */
import { useState, useEffect, useCallback } from 'react';
import { 
  ShoppingCart, RefreshCw, Settings2,
  Mail, MessageSquare, Bell, 
  Clock, Eye, BarChart3, ChevronRight, Loader2, Percent
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  CartRecoveryMetrics,
  CartRecoveryChannels
} from './cart-recovery';
import { api } from '@/utils/apiClient';

export default function CartRecoveryTab() {
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [settings, setSettings] = useState(null);
  const [abandonedCarts, setAbandonedCarts] = useState([]);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [activeView, setActiveView] = useState('overview');
  const [schedulerStatus, setSchedulerStatus] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [analyticsRes, settingsRes, cartsRes, schedulerRes] = await Promise.all([
        api.get('/admin/cart-recovery/analytics', { params: { days: 30 } }),
        api.get('/admin/cart-recovery/settings'),
        api.get('/admin/cart-recovery/abandoned-carts', { params: { limit: 20 } }),
        api.get('/admin/cart-recovery/scheduler-status')
      ]);

      setAnalytics(analyticsRes.data);
      setSettings(settingsRes.data);
      setAbandonedCarts(cartsRes.data.carts || []);
      setSchedulerStatus(schedulerRes.data);
    } catch (err) {
      toast.error('Failed to load cart recovery data');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await api.put('/admin/cart-recovery/settings', settings);
      toast.success('Settings saved');
      setShowSettingsModal(false);
    } catch (err) {
      toast.error('Failed to save settings');
    }
    setSavingSettings(false);
  };

  const sendManualRecovery = async (cartId, channel) => {
    try {
      await api.post(`/admin/cart-recovery/send/${cartId}`, null, { params: { channel } });
      toast.success(`${channel.toUpperCase()} recovery sent`);
      fetchData();
    } catch (err) {
      toast.error('Failed to send recovery');
    }
  };

  if (loading && !analytics) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="cart-recovery-tab">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-heading text-foreground">Cart Recovery</h2>
          <p className="text-muted-foreground">Recover abandoned carts and increase revenue</p>
        </div>
        <div className="flex items-center gap-3">
          <Button onClick={fetchData} variant="outline" className="gap-2">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button onClick={() => setShowSettingsModal(true)} className="gap-2">
            <Settings2 className="w-4 h-4" />
            Settings
          </Button>
        </div>
      </div>

      {/* View Tabs */}
      <div className="flex gap-2 border-b border-border pb-2">
        {[
          { id: 'overview', label: 'Overview', icon: BarChart3 },
          { id: 'carts', label: 'Abandoned Carts', icon: ShoppingCart }
        ].map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setActiveView(id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
              activeView === id 
                ? 'bg-primary text-primary-foreground' 
                : 'text-muted-foreground hover:bg-muted'
            }`}
          >
            <Icon className="w-4 h-4" />
            {label}
          </button>
        ))}
      </div>

      {/* Scheduler Status */}
      {schedulerStatus && (
        <div className={`p-4 rounded-lg border ${
          schedulerStatus.running 
            ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800' 
            : 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800'
        }`}>
          <div className="flex items-center gap-2">
            <Clock className={`w-4 h-4 ${schedulerStatus.running ? 'text-green-600' : 'text-amber-600'}`} />
            <span className="font-medium text-foreground">
              Scheduler: {schedulerStatus.running ? 'Running' : 'Stopped'}
            </span>
            {schedulerStatus.next_run && (
              <span className="text-sm text-muted-foreground">
                • Next: {new Date(schedulerStatus.next_run).toLocaleTimeString()}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Overview View */}
      {activeView === 'overview' && analytics && (
        <div className="space-y-6">
          <CartRecoveryMetrics analytics={analytics} />
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <CartRecoveryChannels analytics={analytics} />
            
            {/* Checkout Step Abandonment */}
            <div className="bg-card rounded-xl p-6 border border-border">
              <h3 className="text-lg font-semibold text-foreground mb-4">Where Customers Drop Off</h3>
              <div className="space-y-4">
                {analytics.step_abandonment && Object.entries(analytics.step_abandonment).map(([step, count]) => {
                  const total = Object.values(analytics.step_abandonment).reduce((a, b) => a + b, 0);
                  const percent = total > 0 ? Math.round((count / total) * 100) : 0;
                  
                  return (
                    <div key={step} className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="capitalize text-foreground">{step}</span>
                        <span className="text-muted-foreground">{count} ({percent}%)</span>
                      </div>
                      <div className="h-2 bg-muted dark:bg-muted/50 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-gradient-to-r from-amber-500 to-orange-500 rounded-full transition-all"
                          style={{ width: `${percent}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Daily Trend */}
          {analytics.daily_trend && analytics.daily_trend.length > 0 && (
            <div className="bg-card rounded-xl p-6 border border-border">
              <h3 className="text-lg font-semibold text-foreground mb-4">7-Day Trend</h3>
              <div className="flex items-end justify-between h-40 gap-2">
                {analytics.daily_trend.map((day, idx) => {
                  const maxVal = Math.max(...analytics.daily_trend.map(d => d.abandoned + d.recovered + d.completed)) || 1;
                  
                  return (
                    <div key={day.date || idx} className="flex-1 flex flex-col items-center gap-2">
                      <div className="w-full flex flex-col-reverse gap-1" style={{ height: '120px' }}>
                        <div 
                          className="w-full bg-red-400 dark:bg-red-500 rounded-t"
                          style={{ height: `${(day.abandoned / maxVal) * 100}%` }}
                          title={`Abandoned: ${day.abandoned}`}
                        />
                        <div 
                          className="w-full bg-green-500"
                          style={{ height: `${(day.recovered / maxVal) * 100}%` }}
                          title={`Recovered: ${day.recovered}`}
                        />
                        <div 
                          className="w-full bg-blue-500 rounded-b"
                          style={{ height: `${(day.completed / maxVal) * 100}%` }}
                          title={`Completed: ${day.completed}`}
                        />
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {new Date(day.date).toLocaleDateString('en-US', { weekday: 'short' })}
                      </span>
                    </div>
                  );
                })}
              </div>
              <div className="flex justify-center gap-6 mt-4 text-xs text-foreground">
                <span className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded bg-blue-500" /> Completed
                </span>
                <span className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded bg-green-500" /> Recovered
                </span>
                <span className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded bg-red-400" /> Abandoned
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Abandoned Carts View */}
      {activeView === 'carts' && (
        <div className="bg-card rounded-xl border border-border overflow-hidden">
          <div className="p-4 border-b border-border">
            <h3 className="font-semibold text-foreground">Abandoned Carts</h3>
            <p className="text-sm text-muted-foreground">Carts waiting for recovery</p>
          </div>
          
          {abandonedCarts.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              <ShoppingCart className="w-12 h-12 mx-auto mb-2 opacity-30" />
              <p>No abandoned carts</p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {abandonedCarts.map((cart) => (
                <div 
                  key={cart.id || cart._id || cart.session_id} 
                  className="p-4 hover:bg-muted/30 dark:hover:bg-muted/20 transition-colors"
                >
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center shrink-0">
                          <ShoppingCart className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                        </div>
                        <div>
                          <p className="font-medium text-foreground">
                            {cart.customer_name || cart.customer_email || cart.customer_phone || 'Anonymous'}
                          </p>
                          <div className="flex items-center gap-3 text-sm text-muted-foreground">
                            <span>{cart.items?.length || 0} items</span>
                            <span className="font-medium text-foreground">${cart.subtotal?.toFixed(2)}</span>
                            <span>•</span>
                            <span>{new Date(cart.abandoned_at || cart.created_at).toLocaleString()}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2 flex-wrap justify-end">
                      {cart.push_sent && (
                        <span className="px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                          Push sent
                        </span>
                      )}
                      {cart.sms_sent && (
                        <span className="px-2 py-1 rounded-full text-xs bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300">
                          SMS sent
                        </span>
                      )}
                      {cart.email_sent && (
                        <span className="px-2 py-1 rounded-full text-xs bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">
                          Email sent
                        </span>
                      )}
                      
                      {!cart.sms_sent && cart.customer_phone && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => sendManualRecovery(cart.id, 'sms')}
                        >
                          <MessageSquare className="w-4 h-4 mr-1" />
                          SMS
                        </Button>
                      )}
                      {!cart.email_sent && cart.customer_email && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => sendManualRecovery(cart.id, 'email')}
                        >
                          <Mail className="w-4 h-4 mr-1" />
                          Email
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Settings Modal */}
      <Dialog open={showSettingsModal} onOpenChange={setShowSettingsModal}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-foreground">Cart Recovery Settings</DialogTitle>
          </DialogHeader>
          
          {settings && (
            <div className="space-y-6">
              {/* Guest Discount */}
              <div className="bg-green-50 dark:bg-green-900/20 rounded-xl p-4 border border-green-200 dark:border-green-800">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <Percent className="w-5 h-5 text-green-600 dark:text-green-400" />
                    <div>
                      <p className="font-medium text-foreground">Guest Discount</p>
                      <p className="text-sm text-muted-foreground">Offer discount to recover guest carts</p>
                    </div>
                  </div>
                  <Switch
                    checked={settings.guest_discount_enabled}
                    onCheckedChange={(checked) => setSettings({...settings, guest_discount_enabled: checked})}
                  />
                </div>
                {settings.guest_discount_enabled && (
                  <div className="flex items-center gap-3">
                    <Label className="text-sm text-muted-foreground">Discount %:</Label>
                    <Input
                      type="number"
                      value={settings.guest_discount_percent}
                      onChange={(e) => setSettings({...settings, guest_discount_percent: parseFloat(e.target.value)})}
                      className="w-24 bg-background"
                      min="0"
                      max="50"
                    />
                  </div>
                )}
              </div>

              {/* Channel Settings */}
              {[
                { key: 'push', label: 'Push Notifications', icon: Bell, color: 'blue', desc: 'Browser push for subscribed users' },
                { key: 'sms', label: 'SMS', icon: MessageSquare, color: 'green', desc: 'Text message recovery' },
                { key: 'email', label: 'Email', icon: Mail, color: 'purple', desc: 'Email recovery with cart summary' }
              ].map(({ key, label, icon: Icon, color, desc }) => (
                <div key={key} className="bg-card rounded-xl p-4 border border-border">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <Icon className={`w-5 h-5 text-${color}-600 dark:text-${color}-400`} />
                      <div>
                        <p className="font-medium text-foreground">{label}</p>
                        <p className="text-sm text-muted-foreground">{desc}</p>
                      </div>
                    </div>
                    <Switch
                      checked={settings[`${key}_enabled`]}
                      onCheckedChange={(checked) => setSettings({...settings, [`${key}_enabled`]: checked})}
                    />
                  </div>
                  {settings[`${key}_enabled`] && (
                    <div className="space-y-3">
                      <div className="flex items-center gap-3">
                        <Label className="text-sm w-24 text-muted-foreground">Delay:</Label>
                        <Input
                          type="number"
                          value={settings[`${key}_delay_minutes`]}
                          onChange={(e) => setSettings({...settings, [`${key}_delay_minutes`]: parseInt(e.target.value)})}
                          className="w-20 bg-background"
                          min="1"
                        />
                        <span className="text-sm text-muted-foreground">minutes</span>
                      </div>
                      {key === 'email' ? (
                        <div>
                          <Label className="text-sm text-muted-foreground">Subject:</Label>
                          <Input
                            value={settings.email_subject || ''}
                            onChange={(e) => setSettings({...settings, email_subject: e.target.value})}
                            className="mt-1 bg-background"
                          />
                        </div>
                      ) : (
                        <div>
                          <Label className="text-sm text-muted-foreground">Message:</Label>
                          <Textarea
                            value={settings[`${key}_message`] || ''}
                            onChange={(e) => setSettings({...settings, [`${key}_message`]: e.target.value})}
                            rows={2}
                            className="mt-1 bg-background"
                            placeholder={key === 'sms' ? "Use {recovery_url} for the link" : ''}
                          />
                          {key === 'sms' && (
                            <p className="text-xs text-muted-foreground mt-1">Use {'{recovery_url}'} to insert the recovery link</p>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}

              {/* Save Button */}
              <div className="flex justify-end gap-3 pt-4 border-t border-border">
                <Button variant="outline" onClick={() => setShowSettingsModal(false)}>
                  Cancel
                </Button>
                <Button onClick={saveSettings} disabled={savingSettings}>
                  {savingSettings ? 'Saving...' : 'Save Settings'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
